import { z } from "zod";

/**
 * Marketing tracking: Google Analytics (GA4) and Facebook (Meta pixel and Conversions API) on the public
 * booking pages only, never the staff app. A conversion ("purchase") is sent from the server when a
 * booking goes live: its reference and total. Services and matching details (hashed email and phone,
 * IP address, browser) only when switched on.
 */

/** What a public booking page needs to load the tags (no secrets). */
export interface PublicTracking {
  googleMeasurementId: string | null;
  facebookPixelId: string | null;
}

export interface MarketingTrackingView {
  enabled: boolean;
  googleMeasurementId: string | null;
  facebookPixelId: string | null;
  /** Whether a secret key is saved (never shown back). */
  hasGoogleApiSecret: boolean;
  hasFacebookAccessToken: boolean;
  includeServices: boolean;
  sendMatching: boolean;
  /** Latest sends, newest first. */
  recent: { id: string; destination: "google" | "facebook"; event: string; reference: string; status: "queued" | "sent" | "failed"; error: string | null; at: string }[];
}

const optionalId = (re: RegExp, message: string) =>
  z
    .string()
    .trim()
    .transform((v) => v || null)
    .pipe(z.string().regex(re, message).nullable());

export const marketingTrackingSchema = z.object({
  enabled: z.boolean(),
  googleMeasurementId: optionalId(/^G-[A-Z0-9]{4,20}$/, "A Google measurement ID looks like G-ABC123XYZ"),
  facebookPixelId: optionalId(/^\d{6,20}$/, "A Facebook pixel ID is a long number"),
  /** New secret keys; empty leaves the saved one. */
  googleApiSecret: z.string().trim().max(200).default(""),
  facebookAccessToken: z.string().trim().max(1000).default(""),
  includeServices: z.boolean().default(false),
  sendMatching: z.boolean().default(false),
});
export type MarketingTrackingInput = z.infer<typeof marketingTrackingSchema>;

/** Sent with a public booking: the ad platforms' cookies, so the conversion is tied to the visit. */
export const bookingTrackingSchema = z
  .object({
    gaClientId: z.string().max(100).optional(),
    fbp: z.string().max(200).optional(),
    fbc: z.string().max(500).optional(),
    url: z.string().max(1000).optional(),
  })
  .optional();
